import { Locales } from '@griply/types';
import { formatDurationLabel } from '@griply/utils';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TimerClockFast } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { TimePickerMenu } from 'shared/components/ui/time-picker-menu';
import { durationOptions } from 'shared/constants';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { InteractionType } from 'shared/types/data-type';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { isNumber } from 'shared/utils/is-number';

import * as Styled from './task-meta-interaction-duration.style';

export type TaskMetaInteractionDurationProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  onChange: (duration: number | null) => void;
  value?: number | null;
  hasIcon?: boolean;
  highlight?: boolean;
};

export const TaskMetaInteractionDuration: React.FC<
  TaskMetaInteractionDurationProps
> = ({
  onChange,
  value,
  hasIcon,
  highlight,

  ...rest
}) => {
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const _onChange = (time?: number) => {
    onChange(time ?? null);
    closeMenu();
  };

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        color={
          highlight && isNumber(value) ? Colors.TextPrimary : Colors.Inherit
        }
        start={hasIcon ? <Icon icon={TimerClockFast} /> : undefined}
        onClick={openMenu}
        label={
          isNumber(value)
            ? formatDurationLabel(value!, locale === Locales.NlNL)
            : undefined
        }
        selected={menuOpen}
        tooltipLabel={t(
          value
            ? 'task.timeslot.duration.tooltip.edit.label'
            : 'task.timeslot.duration.tooltip.add.label',
        )}
        {...rest}
      />

      {menuOpen && (
        <TimePickerMenu
          referenceElement={containerRef}
          interactionType={InteractionType.Task}
          value={value}
          timeFormat={TimeFormatOptions.Military}
          options={durationOptions}
          onChange={_onChange}
          onCancel={closeMenu}
          optionIcon={TimerClockFast}
          placeholder={t('forms.task.fields.duration.placeholder')}
          clearLabel={t('task.timeslot.duration.options.clear.label')}
          hasCustomInput
          canClear
        />
      )}
    </Styled.Container>
  );
};
